import {IconButton} from '@dropbox/dig-components/buttons';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack, ThemeContainer, ThemeProvider} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  BeakerLine,
  CatLine,
  EmojiSmileLine,
  FlagLine,
  FootballLine,
  HeartLine,
  LightbulbLine,
  TravelLine,
} from '@dropbox/dig-icons/assets';
import data from '@emoji-mart/data';
import {derivedThemeAtom} from 'atoms/layout';
import {Eyebrow} from 'components/DSYS/Eyebrow';
import {init, SearchIndex} from 'emoji-mart';
import {useAtomValue} from 'jotai';
import {useCallback, useEffect, useRef, useState} from 'react';

export const isDropboxEmoji = (src: string) =>
  src.includes('paper.dropboxstatic.com/static/img/ace/emoji');

export const getUrlForEmoji = (hexCode: string) =>
  `https://paper.dropboxstatic.com/static/img/ace/emoji/${hexCode}.png?version=8.0.0`;

// Helper function to get emoji image URL
export const getEmojiImageUrl = (emoji: Emoji) =>
  getUrlForEmoji(emoji.skins?.[0]?.unified?.toLowerCase() || emoji.id);

let isInitialized = false;

export type Emoji = any;

interface EmojiPickerProps {
  searchQuery: string;
  selectedIndex: number;
  onEmojiSelect: (emoji: Emoji) => void;
  onSelectedIndexChange: (index: number) => void;
  onEnterPress?: number;
}

const CATEGORIES = [
  {
    id: 'people',
    name: 'Smileys & People',
    icon: EmojiSmileLine,
  },
  {
    id: 'nature',
    name: 'Animals & Nature',
    icon: CatLine,
  },
  {
    id: 'foods',
    name: 'Food & Drink',
    icon: BeakerLine,
  },
  {
    id: 'activity',
    name: 'Activity',
    icon: FootballLine,
  },
  {
    id: 'places',
    name: 'Travel & Places',
    icon: TravelLine,
  },
  {
    id: 'objects',
    name: 'Objects',
    icon: LightbulbLine,
  },
  {
    id: 'symbols',
    name: 'Symbols',
    icon: HeartLine,
  },
  {
    id: 'flags',
    name: 'Flags',
    icon: FlagLine,
  },
];

// Filter function matching the EmojiPicker filter
// Skip compound emojis (those with ZWJ - Zero Width Joiner 200d)
// Also skip emojis with variation selector fe0f
export const shouldConvertEmoji = (hexCode: string): boolean => {
  return !hexCode.includes('200d') && !hexCode.includes('fe0f');
};

const emojiFilter = (emoji?: Emoji) => {
  const unified = emoji?.skins?.[0]?.unified?.toLowerCase() || emoji?.id;
  return unified && shouldConvertEmoji(unified);
};

export const EmojiPicker = ({
  searchQuery,
  selectedIndex,
  onEmojiSelect,
  onSelectedIndexChange,
  onEnterPress,
}: EmojiPickerProps) => {
  const [emojis, setEmojis] = useState<Emoji[]>([]);
  const [emojisByCategory, setEmojisByCategory] = useState<Record<string, Emoji[]>>({});
  const [activeCategory, setActiveCategory] = useState('people');
  const gridRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);
  const previousEnterPressRef = useRef(0);

  // Get all emojis in a flat list for keyboard navigation
  const allEmojis = searchQuery !== '' ? emojis : Object.values(emojisByCategory).flat();

  // Handle Enter key press to select current emoji
  useEffect(() => {
    // Only trigger if enterPressed changed and is > 0
    if (onEnterPress && onEnterPress > 0 && onEnterPress !== previousEnterPressRef.current) {
      previousEnterPressRef.current = onEnterPress;

      if (allEmojis.length > 0) {
        const currentEmoji = allEmojis[Math.min(selectedIndex, allEmojis.length - 1)];
        if (currentEmoji) {
          onEmojiSelect(currentEmoji);
        }
      }
    }
  }, [onEnterPress, allEmojis, selectedIndex, onEmojiSelect]);

  // Initialize emoji-mart data once
  useEffect(() => {
    if (!isInitialized) {
      init({data}).then(() => {
        isInitialized = true;
      });
    }
  }, []);

  // Search for emojis when query changes
  useEffect(() => {
    const searchEmojis = async () => {
      if (searchQuery === '') {
        // Fetch emojis by category - use emoji-mart's category data directly
        const categorized: Record<string, Emoji[]> = {};

        // Get all categories from emoji-mart data
        const categories = (data as any).categories || [];

        for (const category of categories) {
          const emojiIds = category.emojis || [];
          const categoryEmojis = emojiIds
            .map((id: string) => (data as any).emojis[id])
            .filter(emojiFilter);

          // Map our category IDs to emoji-mart's category IDs
          if (category.id === 'people') {
            categorized.people = categoryEmojis;
          } else if (category.id === 'nature') {
            categorized.nature = categoryEmojis;
          } else if (category.id === 'foods') {
            categorized.foods = categoryEmojis;
          } else if (category.id === 'activity') {
            categorized.activity = categoryEmojis;
          } else if (category.id === 'places') {
            categorized.places = categoryEmojis;
          } else if (category.id === 'objects') {
            categorized.objects = categoryEmojis;
          } else if (category.id === 'symbols') {
            categorized.symbols = categoryEmojis;
          } else if (category.id === 'flags') {
            categorized.flags = categoryEmojis;
          }
        }

        setEmojisByCategory(categorized);
        setEmojis([]);
      } else {
        const results = await SearchIndex.search(searchQuery);
        setEmojis((results || []).filter(emojiFilter).slice(0, 100)); // Show up to 100 search results
        setEmojisByCategory({});
      }
    };

    searchEmojis();
  }, [searchQuery]);

  // Reset selectedIndex if it's out of bounds
  useEffect(() => {
    if (allEmojis.length > 0 && selectedIndex >= allEmojis.length) {
      onSelectedIndexChange(0);
    } else if (selectedIndex < 0 && allEmojis.length > 0) {
      onSelectedIndexChange(allEmojis.length - 1);
    }
  }, [selectedIndex, allEmojis.length, onSelectedIndexChange]);

  // Scroll selected emoji into view
  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({block: 'nearest', behavior: 'smooth'});
    }
  }, [selectedIndex]);

  const handleCategoryClick = (categoryId: string) => {
    setActiveCategory(categoryId);
    const categoryElement = document.getElementById(`category-${categoryId}`);
    if (categoryElement && gridRef.current) {
      gridRef.current.scrollTop = categoryElement.offsetTop - 1;
    }
  };

  const isSearching = searchQuery !== '';

  // Track active category based on scroll position
  useScrollCategoryTracker(gridRef, isSearching, emojisByCategory, setActiveCategory);

  return (
    <ThemeProvider mode={useAtomValue(derivedThemeAtom) === 'bright' ? 'dark' : 'bright'}>
      <ThemeContainer>
        <Box
          id="emoji-picker"
          display="flex"
          backgroundColor="Secondary Surface - State 1"
          borderStyle="Solid"
          borderColor="Border Subtle"
          borderRadius="Medium"
          overflow="hidden"
          style={{
            width: 300,
            height: 314,
          }}
        >
          <Stack
            paddingX="8"
            paddingY="4"
            className={atoms({
              display: 'flex',
              backgroundColor: 'Background Raised',
              alignItems: 'flex-end',
            })}
            gap="2"
          >
            {CATEGORIES.map((category) => (
              <Box key={category.id} visibility={!isSearching ? 'visible' : 'hidden'}>
                <IconButton
                  variant="borderless"
                  onClick={() => handleCategoryClick(category.id)}
                  title={category.name}
                  aria-label="icon button"
                >
                  <UIIcon
                    src={category.icon}
                    className={atoms({
                      color: activeCategory === category.id ? 'Primary Base' : 'Text Subtle',
                    })}
                  />
                </IconButton>
              </Box>
            ))}
          </Stack>

          <Box
            ref={gridRef}
            overflowY="auto"
            display="flex"
            flexDirection="column"
            height="100%"
            width="100%"
          >
            {isSearching ? (
              emojis.length > 0 ? (
                <Box
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '2px',
                  }}
                  padding="8"
                >
                  {emojis.map((emoji, index) => (
                    <EmojiButton
                      key={emoji.id + index}
                      emoji={emoji}
                      selected={index === selectedIndex}
                      selectedRef={selectedRef}
                      onEmojiSelect={onEmojiSelect}
                      onSelectedIndexChange={() => onSelectedIndexChange(index)}
                    />
                  ))}
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" padding="32">
                  <Box paddingBottom="8">
                    <Text style={{fontSize: '48px', opacity: 0.3}}>😕</Text>
                  </Box>
                  <Text size="small">No emojis found</Text>
                </Box>
              )
            ) : (
              CATEGORIES.map((category) => (
                <Stack key={category.id} id={`category-${category.id}`} gap="4">
                  <Box
                    position="sticky"
                    backgroundColor="Background Raised"
                    top="0"
                    paddingX="4"
                    paddingY="8"
                    style={{zIndex: 1}}
                  >
                    <Eyebrow>{category.name}</Eyebrow>
                  </Box>
                  {!emojisByCategory[category.id]?.length && (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      padding="116"
                      marginBottom="36"
                    >
                      <Spinner size="small" monochromatic />
                    </Box>
                  )}
                  <Box
                    paddingX="8"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      gap: '2px',
                    }}
                  >
                    {(emojisByCategory[category.id] || []).map((emoji, index) => (
                      <EmojiButton
                        key={emoji.id + index}
                        emoji={emoji}
                        selected={index === selectedIndex}
                        onEmojiSelect={onEmojiSelect}
                        onSelectedIndexChange={() => onSelectedIndexChange(index)}
                      />
                    ))}
                  </Box>
                </Stack>
              ))
            )}
          </Box>
        </Box>
      </ThemeContainer>
    </ThemeProvider>
  );
};

const EmojiButton = ({
  emoji,
  selected,
  selectedRef,
  onEmojiSelect,
  onSelectedIndexChange,
}: {
  emoji: Emoji;
  selected: boolean;
  selectedRef?: React.RefObject<HTMLButtonElement>;
  onEmojiSelect: (emoji: Emoji) => void;
  onSelectedIndexChange: () => void;
}) => (
  <IconButton
    variant="borderless"
    className={atoms({
      backgroundColor: selected ? 'Background Base' : undefined,
      outline: selected ? 'Focus Outline' : undefined,
    })}
    style={{outlineOffset: selected ? '-2px' : undefined}}
    ref={selected ? selectedRef : undefined}
    onClick={() => onEmojiSelect(emoji)}
    onMouseEnter={() => onSelectedIndexChange()}
    title={emoji.name}
    aria-label="icon button"
  >
    <img
      src={getEmojiImageUrl(emoji)}
      alt={emoji.name}
      style={{width: 22, height: 22, display: 'block'}}
      loading="lazy"
      onError={(e) => {
        // Hide broken images
        // eslint-disable-next-line no-param-reassign
        (e.target as HTMLImageElement).style.display = 'none';
      }}
    />
  </IconButton>
);

// Hook to track active category based on scroll position
const useScrollCategoryTracker = (
  gridRef: React.RefObject<HTMLDivElement>,
  isSearching: boolean,
  emojisByCategory: Record<string, Emoji[]>,
  setActiveCategory: (category: string) => void
) => {
  const handleScroll = useCallback(() => {
    if (isSearching || !gridRef.current) return;

    const scrollTop = gridRef.current.scrollTop;

    // Find the current category based on scroll position
    let currentCategory = null;

    // Iterate from bottom to top to find which category we've scrolled past
    for (let i = CATEGORIES.length - 1; i >= 0; i--) {
      const categoryId = CATEGORIES[i].id;
      const element = document.getElementById(`category-${categoryId}`);

      if (element) {
        const elementTop = element.offsetTop;

        if (scrollTop >= elementTop) {
          currentCategory = categoryId;
          break;
        }
      }
    }

    // If we're at the very top, activate the first category
    if (scrollTop < 0 && CATEGORIES.length > 0) {
      currentCategory = CATEGORIES[0].id;
    }

    if (currentCategory) {
      setActiveCategory(currentCategory);
    }
  }, [isSearching, gridRef, setActiveCategory]);

  useEffect(() => {
    if (isSearching || !gridRef.current) return;

    let rafId: number;
    const scrollContainer = gridRef.current;

    const scrollHandler = () => {
      if (rafId) return; // Skip if already scheduled

      rafId = requestAnimationFrame(() => {
        handleScroll();
        rafId = 0;
      });
    };

    // Add scroll listener
    scrollContainer.addEventListener('scroll', scrollHandler, {passive: true});

    // Initial call
    scrollHandler();

    return () => {
      scrollContainer.removeEventListener('scroll', scrollHandler);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isSearching, emojisByCategory, handleScroll, gridRef]);
};
